import {
  createActions,
  createReqConsts,
  createReqReducer,
  createReqWatcher,
  initialState as rootState,
} from '../../../utils/redux';

import {
  findAndReplace,
  formatAppointmentList,
  markOverdueAppointmentList,
  toggleHighlightStatus,
  transformTimeAdapter,
} from '../../../utils/dashboard/handleOverdueAppointments';

import * as changeRoom from './change-room-req';
import * as changeStatus from './change-status-req';
import * as pinAppointment from './pin-req';
import { DASHBOARD_FILTER_TYPES } from '../filter-types';
import { consts as CANCEL_APPOINTMENT } from '../../healthboard/requests/cancel-appointment-req';
import { consts as STATUS_CHANGE } from '../../healthboard/requests/status-change-req';
import { consts as EDIT_PAYMENT_CONST } from '../../healthboard/requests/edit-payment-req';
import { consts as CHANGE_STATUS_TO_PAID_CONST } from './change-paid-status-req';
import { consts as UPLOAD_DOCUMENT_CONST } from '../../healthboard/requests/upload-document-req';
import { consts as DELETE_DOCUMENT } from '../../healthboard/requests/delete-document-req';
import { consts as SURVEY_COMPLETIONS } from '../../surveys/upcoming-survey-completions';
import { handleUpdateStatusSurveyInDetails } from '../../../components/Dashboard/DashboardTable/utils/handleUpdateStatusSurveyInDetails';
import { APPOINTMENT_DETAILS_TYPES } from '../../healthboard/appointment-details';

export const consts = createReqConsts('dashboard', 'appointment-list-req', [
  'INITIAL_LOAD',
  'ADD_NEW_STATUS',
  'REMOVE_NEW_STATUS',
  'PAGE_LEAVE',
  'MARK_OVERDUE',
  'INSERT',
  'UPDATE_VIA_WS',
  'PIN_VIA_WS',
  'REMOVE_ITEM',
  'SET_ACTIVE_APPOINTMENT',
]);
export const actions = createActions(consts);
export const watcher = createReqWatcher(consts, actions, {
  endpoint: 'dashboard',
  type: 'get',
  slug: [
    'appointments',
    {
      type: 'payload.query',
      queryParameter: 'date',
      selector: 'date',
    },
    {
      type: 'payload.query',
      queryParameter: 'status_ids',
      selector: 'statuses',
    },
    {
      type: 'payload.query',
      queryParameter: 'service_ids',
      selector: 'services',
    },
    {
      type: 'payload.query',
      queryParameter: 'location_ids',
      selector: 'locations',
    },
  ],
});

const defaultRequestReducer = createReqReducer(consts);

const initialState = {
  ...rootState,
  list: [],
};

export default (state = initialState, action) => {
  const { type, payload } = action;

  switch (type) {
    case consts.INSERT: {
      return {
        ...state,
        list: formatAppointmentList([payload, ...state.list]),
      };
    }

    case DASHBOARD_FILTER_TYPES.SET_FILTER: {
      return {
        ...state,
        sending: true,
      };
    }

    case consts.SUCCEED: {
      return {
        ...state,
        sending: false,
        succeed: true,
        list: formatAppointmentList(payload),
      };
    }

    case consts.MARK_OVERDUE: {
      return {
        ...state,
        list: markOverdueAppointmentList(state.list),
      };
    }

    case consts.ADD_NEW_STATUS: {
      const list = toggleHighlightStatus(state.list, payload.id, true);

      return { ...state, list };
    }

    case consts.REMOVE_NEW_STATUS: {
      const list = toggleHighlightStatus(state.list, payload.id, false);

      return { ...state, list };
    }

    case consts.PAGE_LEAVE: {
      return initialState;
    }

    case pinAppointment.consts.SUCCEED:
    case consts.PIN_VIA_WS: {
      const list = findAndReplace(state.list, payload);
      return {
        ...state,
        list: formatAppointmentList(list),
      };
    }

    case consts.UPDATE_VIA_WS: {
      const list = findAndReplace(state.list, payload, true);
      return {
        ...state,
        list: formatAppointmentList(list),
      };
    }

    case consts.REMOVE_ITEM: {
      const list = state.list.filter(item => item.id !== payload.id);
      return {
        ...state,
        list,
      };
    }

    case changeStatus.consts.SUCCEED:
    case changeRoom.consts.SUCCEED: {
      const updatedAppointment = transformTimeAdapter(payload);
      const list = findAndReplace(state.list, updatedAppointment, true);

      return {
        ...state,
        list: markOverdueAppointmentList(list),
      };
    }

    case EDIT_PAYMENT_CONST.SUCCEED: {
      const target = state.list.find(({ id }) => action.payload.id === id);
      if (!target) return state;
      const isFullEntity = !!state.activeAppointment;

      return {
        ...state,
        ...(isFullEntity && {
          activeAppointment: {
            ...state.activeAppointment,
            copay: action.payload.copay,
          },
        }),
        list: findAndReplace(state.list, {
          ...target,
          copay: action.payload.copay,
        }),
      };
    }

    case CHANGE_STATUS_TO_PAID_CONST.SUCCEED: {
      const target = state.list.find(({ id }) => action.payload.id === id);
      if (!target) return state;

      return {
        ...state,
        list: findAndReplace(state.list, {
          ...target,
          paymentStatus: action.payload.paymentStatus,
        }),
      };
    }

    case consts.SET_ACTIVE_APPOINTMENT: {
      return {
        ...state,
        activeAppointment: action.payload,
      };
    }

    case UPLOAD_DOCUMENT_CONST.SUCCEED: {
      const target = state.activeAppointment;
      if (target) target.documents.push(action.payload);

      return {
        ...state,
        activeAppointment: {
          ...target,
        },
      };
    }

    case DELETE_DOCUMENT.UPDATE: {
      const target = state.activeAppointment;

      if (target) {
        const targetDocument = target.documents.findIndex(
          ({ id }) => action.payload.documentId === id,
        );
        target.documents.splice(targetDocument, 1);

        return {
          ...state,
          activeAppointment: {
            ...target,
          },
        };
      }
      return state;
    }

    case SURVEY_COMPLETIONS.SUCCEED: {
      return handleUpdateStatusSurveyInDetails(state, action);
    }

    case STATUS_CHANGE.SUCCEED: {
      const target = state.list.find(({ id }) => action.payload.id === id);
      if (!target) return state;

      return {
        ...state,
        activeAppointment: {
          ...state.activeAppointment,
          status: action.payload.status,
        },
        list: findAndReplace(state.list, {
          ...target,
          status: action.payload.status,
        }),
      };
    }

    case CANCEL_APPOINTMENT.SUCCEED:
    case APPOINTMENT_DETAILS_TYPES.SLICE_UPCOMING_APPOINTMENT: {
      const newList = state.list.filter(({ id }) => action.payload.id !== id);

      return {
        ...state,
        activeAppointment: undefined,
        list: newList,
      };
    }

    default: {
      return defaultRequestReducer(state, action);
    }
  }
};



// WEBPACK FOOTER //
// ./src/redux/dashboard/requests/appointment-list-req.js